import * as THREE from 'three'
import { toMatrixTuple } from '../../imported-model/loaders/assembly-node-utils'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { normalizeDisplayNodeName } from './displaySnapshotObjects'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
} from './types'

/**
 * Puppy 装配 display.glb 语义树物化器。
 *
 * 本类负责把 assembly.json 的 occurrence 层级、局部矩阵和 display 归属一次性写回
 * Three.js 导出树，并在返回前验证每个装配节点都有独立运行时承载对象。
 */
export class PuppyAssemblyDisplayTreeMaterializer {
  /**
   * 按权威 assembly 文档物化 display.glb 树。
   *
   * @param input display 导出根、装配文档与 display 归属。
   */
  public materialize(input: {
    readonly root: THREE.Group
    readonly assembly: PuppyAssemblyDocument
    readonly ownerships: readonly {
      readonly displayNodeId: string
      readonly ownerNodeId: string
    }[]
  }): void {
    this.completeAssemblyNodeTransformsFromDisplay(input.root, input.assembly)
    this.assertAssemblyNodeTransforms(input.assembly.nodes)
    this.rebuildDisplayTreeByAssembly(input)
    this.assertAssemblyRuntimeCarrierGraph(input.root, input.assembly)
  }

  /**
   * 从 display.glb 导出根中收集 display node 对象。
   *
   * @param root display.glb 导出根。
   * @returns display node ID 到 Object3D 的索引。
   */
  private collectDisplayObjectByDisplayNodeId(root: THREE.Object3D): Map<string, THREE.Object3D> {
    const objectByDisplayNodeId = new Map<string, THREE.Object3D>()
    root.traverse((object) => {
      const puppy = ImportedModelObjectMetadataService.readPuppyOwnerMetadata(object)
      const displayNodeId = typeof puppy?.displayNodeId === 'string'
        ? puppy.displayNodeId.trim()
        : ''
      if (displayNodeId.length > 0) {
        objectByDisplayNodeId.set(displayNodeId, object)
      }
    })
    return objectByDisplayNodeId
  }

  /**
   * 从 display.glb 导出根中收集已经持久化的 assembly node 承载对象。
   *
   * 同一个 assembly node ID 如果出现多个承载对象，说明当前快照无法确定唯一承载体；此时记录为
   * `null`，后续统一创建新的语义组收口，避免依赖遍历顺序产生不稳定结果。
   *
   * @param root display.glb 导出根。
   * @returns assembly node ID 到唯一承载对象的索引；冲突项为 null。
   */
  private collectAssemblyNodeCarrierByNodeId(
    root: THREE.Object3D,
  ): Map<string, THREE.Object3D | null> {
    const carrierByNodeId = new Map<string, THREE.Object3D | null>()
    root.traverse((object) => {
      const puppy = ImportedModelObjectMetadataService.readPuppyOwnerMetadata(object)
      const assemblyNodeId = typeof puppy?.assemblyNodeId === 'string'
        ? puppy.assemblyNodeId.trim()
        : ''
      if (assemblyNodeId.length === 0 || puppy?.assemblyNodeRole !== 'node') {
        return
      }

      if (carrierByNodeId.has(assemblyNodeId)) {
        carrierByNodeId.set(assemblyNodeId, null)
        return
      }
      carrierByNodeId.set(assemblyNodeId, object)
    })
    return carrierByNodeId
  }

  /**
   * 根据已归属的 display 节点补全缺失的 assembly node 本地矩阵。
   *
   * 部分权威 hierarchy 节点来自 shell/body 语义，而不是原始 GLB 中真实存在的装配承载对象，
   * 因此用 display 的世界矩阵相对目标父节点世界矩阵反推出本地矩阵。
   *
   * @param root display.glb 导出根。
   * @param assembly 权威装配文档。
   */
  private completeAssemblyNodeTransformsFromDisplay(
    root: THREE.Object3D,
    assembly: PuppyAssemblyDocument,
  ): void {
    const displayObjectByDisplayNodeId = this.collectDisplayObjectByDisplayNodeId(root)
    const worldMatrixByNodeId = new Map<string, THREE.Matrix4>()
    const identityMatrix = new THREE.Matrix4()

    root.updateWorldMatrix(true, true)
    assembly.nodes.forEach((node) => {
      const parentWorldMatrix = node.parentNodeId
        ? worldMatrixByNodeId.get(node.parentNodeId)
        : identityMatrix
      if (!parentWorldMatrix) {
        throw new Error(`PuppyCAD 离线包装配节点父级缺少本地矩阵: ${node.nodeId} (${node.name})`)
      }

      const localMatrix = new THREE.Matrix4()
      if (node.transform) {
        localMatrix.fromArray(node.transform)
      } else {
        const displayObject = node.displayNodeIds
          .map((displayNodeId) => displayObjectByDisplayNodeId.get(displayNodeId))
          .find((object): object is THREE.Object3D => Boolean(object))
        if (!displayObject) {
          throw new Error(`PuppyCAD 离线包装配节点缺少本地矩阵和显示节点: ${node.nodeId} (${node.name})`)
        }
        displayObject.updateWorldMatrix(true, false)
        localMatrix
          .copy(parentWorldMatrix)
          .invert()
          .multiply(displayObject.matrixWorld)
        node.transform = toMatrixTuple(localMatrix)
      }

      worldMatrixByNodeId.set(
        node.nodeId,
        new THREE.Matrix4().multiplyMatrices(parentWorldMatrix, localMatrix),
      )
    })
  }

  /**
   * 校验最终装配节点都具备本地矩阵。
   *
   * @param nodes 最终装配节点列表。
   */
  private assertAssemblyNodeTransforms(nodes: readonly PuppyAssemblyNode[]): void {
    nodes.forEach((node) => {
      if (!node.transform) {
        throw new Error(`PuppyCAD 离线包装配节点缺少本地矩阵: ${node.nodeId} (${node.name})`)
      }
    })
  }

  /**
   * 创建 assembly node 对应的 display.glb 语义组。
   *
   * @param node assembly node。
   * @returns Three.js Group。
   */
  private createAssemblyNodeDisplayGroup(node: PuppyAssemblyNode): THREE.Group {
    const group = new THREE.Group()
    group.name = normalizeDisplayNodeName(node.name)
    if (node.transform) {
      group.matrix.fromArray(node.transform)
      group.matrix.decompose(group.position, group.quaternion, group.scale)
      group.updateMatrix()
    }
    ImportedModelObjectMetadataService.writePuppyOwnerMetadata(group, {
      assemblyNodeId: node.nodeId,
      assemblyNodeRole: 'node',
    })
    return group
  }

  /**
   * 把权威 assembly node 的局部变换写回 display 承载对象。
   *
   * @param object assembly node 承载对象。
   * @param node 权威 assembly node。
   */
  private applyAssemblyNodeTransformToDisplayCarrier(
    object: THREE.Object3D,
    node: PuppyAssemblyNode,
  ): void {
    if (!node.transform) {
      return
    }
    object.matrix.fromArray(node.transform)
    object.matrix.decompose(object.position, object.quaternion, object.scale)
    object.updateMatrix()
  }

  /**
   * 移除根节点下的所有子对象。
   *
   * @param root display.glb 导出根。
   */
  private clearRootChildren(root: THREE.Object3D): void {
    while (root.children.length > 0) {
      root.remove(root.children[0]!)
    }
  }

  /**
   * 将对象加入新父级并保持世界矩阵不变。
   *
   * @param object 待重挂载对象。
   * @param parent 新父级。
   * @param worldMatrix 重挂载前世界矩阵。
   */
  private addObjectToParentPreservingWorldMatrix(
    object: THREE.Object3D,
    parent: THREE.Object3D,
    worldMatrix: THREE.Matrix4,
  ): void {
    parent.updateWorldMatrix(true, false)
    object.parent?.remove(object)
    parent.add(object)

    const parentWorldInverse = new THREE.Matrix4().copy(parent.matrixWorld).invert()
    const localMatrix = new THREE.Matrix4().multiplyMatrices(parentWorldInverse, worldMatrix)
    object.matrix.copy(localMatrix)
    object.matrix.decompose(object.position, object.quaternion, object.scale)
    object.updateMatrix()
  }

  /**
   * 写入 display 节点的 puppy 归属 metadata。
   *
   * @param object display 节点。
   * @param ownerNodeId 归属 assembly node ID。
   */
  private writeDisplayObjectPuppyOwner(object: THREE.Object3D, ownerNodeId: string): void {
    ImportedModelObjectMetadataService.writePuppyOwnerMetadata(object, {
      assemblyNodeId: ownerNodeId,
      assemblyNodeRole: 'display',
    })
  }

  /**
   * 写入 assembly node 承载对象的 puppy metadata，同时保留显示绑定字段。
   *
   * @param object assembly node 承载对象。
   * @param nodeId assembly node ID。
   */
  private writeAssemblyNodePuppyOwner(object: THREE.Object3D, nodeId: string): void {
    ImportedModelObjectMetadataService.writePuppyOwnerMetadata(object, {
      assemblyNodeId: nodeId,
      assemblyNodeRole: 'node',
    })
  }

  /**
   * 按 assembly.json 重建 display.glb 的语义组层级。
   *
   * @param input 重建输入。
   */
  private rebuildDisplayTreeByAssembly(input: {
    readonly root: THREE.Group
    readonly assembly: PuppyAssemblyDocument
    readonly ownerships: readonly {
      readonly displayNodeId: string
      readonly ownerNodeId: string
    }[]
  }): void {
    input.root.updateWorldMatrix(true, true)
    const displayObjectByDisplayNodeId = this.collectDisplayObjectByDisplayNodeId(input.root)
    const worldMatrixByDisplayNodeId = new Map<string, THREE.Matrix4>()
    displayObjectByDisplayNodeId.forEach((object, displayNodeId) => {
      object.updateWorldMatrix(true, false)
      worldMatrixByDisplayNodeId.set(displayNodeId, object.matrixWorld.clone())
    })

    const persistedCarrierByNodeId = this.collectAssemblyNodeCarrierByNodeId(input.root)
    const carrierByNodeId = new Map(input.assembly.nodes.map((node) => {
      const persistedCarrier = persistedCarrierByNodeId.get(node.nodeId)
      const carrier = persistedCarrier ?? this.createAssemblyNodeDisplayGroup(node)
      this.applyAssemblyNodeTransformToDisplayCarrier(carrier, node)
      this.writeAssemblyNodePuppyOwner(carrier, node.nodeId)
      return [node.nodeId, carrier] as const
    }))
    input.assembly.nodes.forEach((node) => {
      const carrier = carrierByNodeId.get(node.nodeId)
      if (!carrier) {
        return
      }
      const parentCarrier = node.parentNodeId ? carrierByNodeId.get(node.parentNodeId) : null
      if (parentCarrier) {
        parentCarrier.add(carrier)
      }
    })

    const rootCarrier = carrierByNodeId.get(input.assembly.rootNodeId)
    if (!rootCarrier) {
      return
    }

    this.clearRootChildren(input.root)
    input.root.add(rootCarrier)
    input.root.updateWorldMatrix(true, true)

    const ownerNodeIdByDisplayNodeId = new Map(input.ownerships.map((ownership) => [
      ownership.displayNodeId,
      ownership.ownerNodeId,
    ]))
    displayObjectByDisplayNodeId.forEach((object, displayNodeId) => {
      const ownerNodeId = ownerNodeIdByDisplayNodeId.get(displayNodeId) ?? input.assembly.rootNodeId
      const ownerCarrier = carrierByNodeId.get(ownerNodeId) ?? rootCarrier
      if (object === ownerCarrier) {
        this.writeAssemblyNodePuppyOwner(object, ownerNodeId)
        return
      }
      const worldMatrix = worldMatrixByDisplayNodeId.get(displayNodeId) ?? object.matrixWorld.clone()
      this.writeDisplayObjectPuppyOwner(object, ownerNodeId)
      this.addObjectToParentPreservingWorldMatrix(object, ownerCarrier, worldMatrix)
    })

    input.root.updateWorldMatrix(true, true)
  }

  /**
   * 校验每个装配 occurrence 都有独立的 display.glb 运行时承载对象。
   *
   * @param root 已按装配图重建的显示根。
   * @param assembly 待持久化的装配文档。
   */
  private assertAssemblyRuntimeCarrierGraph(
    root: THREE.Object3D,
    assembly: PuppyAssemblyDocument,
  ): void {
    const carrierByNodeId = this.collectAssemblyNodeCarrierByNodeId(root)
    const missingNode = assembly.nodes.find((node) => !carrierByNodeId.has(node.nodeId))
    if (missingNode) {
      throw new Error(
        `PuppyCAD 导出装配节点缺少 display.glb 运行时承载对象: ${missingNode.nodeId}`,
      )
    }
  }
}
